import React, { useState, useEffect } from 'react';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import axios from 'axios';
import {
  TrendingUp, TrendingDown, AlertTriangle, Shield, Brain, ChevronRight,
  Loader2, Eye, Activity, CheckCircle, Clock, Zap
} from 'lucide-react';

const SEVERITY_STYLE = {
  high: 'bg-rose-500/10 border-rose-500/15 text-rose-400',
  medium: 'bg-amber-500/10 border-amber-500/15 text-amber-400',
  low: 'bg-blue-500/10 border-blue-500/15 text-blue-400',
  positive: 'bg-emerald-500/10 border-emerald-500/15 text-emerald-400',
};

const STATE_COLORS = {
  calm_alignment: 'bg-emerald-500', performative_flow: 'bg-emerald-400',
  nervous_system_strain: 'bg-rose-500', cognitive_overload: 'bg-amber-500',
  anxiety_loop: 'bg-rose-400', emotional_fatigue: 'bg-purple-500',
  recovery_debt: 'bg-zinc-400', fragmented_focus: 'bg-amber-400',
  irritable_reactivity: 'bg-rose-600',
};

// ═══════════════════════════════════════════════════════════
// SUBSCRIBER — "What May Happen Next"
// ═══════════════════════════════════════════════════════════

export function TwinFloPredictionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/twinflo/my-predictions`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading) return null;
  if (!data?.predictions?.length) return null;

  return (
    <Card className="bg-adm-surface border-white/[0.06]" data-testid="twinflo-predictions-card">
      <CardContent className="py-4 px-4 space-y-2.5">
        <div className="text-[10px] text-adm-tertiary uppercase tracking-wider font-['Work_Sans']">What May Happen Next</div>
        {data.predictions.map((p, i) => (
          <div key={i} className={`rounded-lg border px-3 py-2 ${p.is_positive ? 'bg-emerald-500/5 border-emerald-500/15' : 'bg-amber-500/5 border-amber-500/15'}`}>
            <div className="flex items-center gap-2 mb-1">
              {p.is_positive ? <CheckCircle className="w-3 h-3 text-emerald-400" /> : <AlertTriangle className="w-3 h-3 text-amber-400" />}
              <span className={`text-xs font-medium ${p.is_positive ? 'text-emerald-400' : 'text-amber-400'}`}>{p.what_may_happen}</span>
              <Badge className={`text-[8px] border-0 ${p.is_positive ? 'bg-emerald-500/15 text-emerald-400' : 'bg-amber-500/15 text-amber-400'}`}>{p.likelihood}</Badge>
            </div>
            {p.watch_for?.length > 0 && (
              <div className="text-[10px] text-adm-tertiary ml-5">
                Watch for: {p.watch_for.join(', ')}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════
// STAFF — Prediction Panel
// ═══════════════════════════════════════════════════════════

export function TwinFloPredictionPanel({ subscriberId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/twinflo/predictions/${subscriberId}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [subscriberId]);

  if (loading) return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 text-adm-accent animate-spin" /></div>;
  if (!data?.predictions?.length) return <div className="text-xs text-adm-tertiary text-center py-2">No active predictions</div>;

  return (
    <div className="space-y-2" data-testid="twinflo-staff-predictions">
      {data.predictions.map((p, i) => (
        <div key={i} className={`rounded-lg border px-3 py-2.5 ${SEVERITY_STYLE[p.severity] || SEVERITY_STYLE.medium}`}>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-['Outfit'] font-medium">{p.label}</span>
            <div className="flex items-center gap-1.5">
              <Badge className="text-[8px] border-0 bg-white/10">{Math.round(p.probability * 100)}%</Badge>
              <Badge className="text-[8px] border-0 bg-white/10">{p.time_horizon}</Badge>
            </div>
          </div>
          <div className="text-[10px] opacity-80 mb-1">Based on: {(p.based_on || []).slice(0, 3).join(', ')}</div>
          {p.early_signals?.length > 0 && (
            <div className="text-[10px] opacity-70">Watch: {p.early_signals.slice(0, 2).join(', ')}</div>
          )}
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// STAFF — Timeline View
// ═══════════════════════════════════════════════════════════

export function TwinFloTimeline({ subscriberId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/twinflo/timeline/${subscriberId}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [subscriberId]);

  if (loading) return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 text-adm-accent animate-spin" /></div>;

  const tl = data?.timeline || [];
  const patterns = data?.patterns || [];
  const trend = data?.trend_summary || {};

  if (!tl.length) return <div className="text-xs text-adm-tertiary text-center py-2">No timeline data</div>;

  return (
    <div className="space-y-3" data-testid="twinflo-timeline">
      {/* Trend header */}
      <div className="flex items-center gap-3">
        <Badge className={`text-[9px] border-0 ${trend.direction === 'improving' ? 'bg-emerald-500/15 text-emerald-400' : trend.direction === 'worsening' ? 'bg-rose-500/15 text-rose-400' : 'bg-zinc-500/15 text-zinc-400'}`}>
          {trend.direction === 'improving' ? <TrendingUp className="w-3 h-3 mr-1" /> : trend.direction === 'worsening' ? <TrendingDown className="w-3 h-3 mr-1" /> : <Activity className="w-3 h-3 mr-1" />}
          {trend.direction}
        </Badge>
        <Badge className="text-[9px] border-0 bg-white/5 text-adm-tertiary">{trend.stability}</Badge>
        <span className="text-[10px] text-adm-tertiary">{tl.length} days</span>
      </div>

      {/* Timeline bars */}
      <div className="flex gap-0.5 items-end h-16">
        {tl.map((t, i) => {
          const reg = t.regulation ?? 50;
          const color = STATE_COLORS[t.state] || 'bg-zinc-500';
          return (
            <div key={i} className="flex-1 flex flex-col items-center gap-0.5" title={`${t.date}: ${t.state?.replace(/_/g, ' ')} (reg: ${reg})`}>
              <div className={`w-full rounded-t ${color}`} style={{ height: `${Math.max(reg * 0.6, 4)}px`, minHeight: '4px' }} />
              {i === 0 || i === tl.length - 1 ? <span className="text-[7px] text-adm-tertiary">{t.date?.slice(5)}</span> : null}
            </div>
          );
        })}
      </div>

      {/* Patterns */}
      {patterns.length > 0 && (
        <div className="space-y-1">
          {patterns.map((p, i) => (
            <div key={i} className="flex items-center gap-1.5 text-[10px] text-adm-tertiary bg-white/[0.02] rounded px-2 py-1">
              <Eye className="w-3 h-3 text-adm-accent flex-shrink-0" />
              <span>{p.description}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// STAFF — Signature Summary
// ═══════════════════════════════════════════════════════════

export function TwinFloSignature({ subscriberId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!subscriberId) return;
    axios.get(`${API}/twinflo/signature/${subscriberId}`).then(r => setData(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [subscriberId]);

  if (loading) return <div className="flex justify-center py-3"><Loader2 className="w-4 h-4 text-adm-accent animate-spin" /></div>;

  const sig = data?.signature_profile;
  if (!sig) return <div className="text-xs text-adm-tertiary text-center py-2">No signature data</div>;

  return (
    <div className="space-y-3" data-testid="twinflo-signature">
      {/* Dominant states */}
      <div>
        <div className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-1">Dominant States</div>
        <div className="flex gap-1 flex-wrap">
          {(sig.dominant_states || []).map((s, i) => (
            <Badge key={i} className="text-[9px] bg-white/[0.06] text-adm-secondary border-0">{s.replace(/_/g, ' ')}</Badge>
          ))}
        </div>
      </div>

      {/* Triggers */}
      {sig.trigger_patterns?.length > 0 && (
        <div>
          <div className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-1">Trigger Patterns</div>
          {sig.trigger_patterns.map((t, i) => (
            <div key={i} className="text-[10px] text-adm-secondary flex items-center gap-1 mb-0.5">
              <Zap className="w-2.5 h-2.5 text-amber-400" /> {t}
            </div>
          ))}
        </div>
      )}

      {/* Recovery + Resilience */}
      <div className="grid grid-cols-2 gap-2">
        <div className="bg-white/[0.02] rounded-lg p-2">
          <div className="text-[9px] text-adm-tertiary mb-0.5">Recovery</div>
          <div className="text-xs text-adm-secondary">Speed: {sig.recovery_pattern?.speed}</div>
          <div className="text-xs text-adm-secondary">Stability: {sig.recovery_pattern?.stability}</div>
        </div>
        <div className="bg-white/[0.02] rounded-lg p-2">
          <div className="text-[9px] text-adm-tertiary mb-0.5">Resilience</div>
          <div className={`text-xs font-medium ${sig.resilience_pattern === 'high' ? 'text-emerald-400' : sig.resilience_pattern === 'moderate' ? 'text-amber-400' : 'text-rose-400'}`}>
            {sig.resilience_pattern}
          </div>
        </div>
      </div>

      {/* Blind Spots */}
      {sig.blind_spots?.length > 0 && (
        <div>
          <div className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-1">Blind Spots</div>
          {sig.blind_spots.map((b, i) => (
            <div key={i} className="text-[10px] text-rose-400/80 flex items-center gap-1 mb-0.5">
              <AlertTriangle className="w-2.5 h-2.5" /> {b}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
